import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { prospectsApi } from '@/api/client';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { salesNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';

export function SalesSubmit() {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await prospectsApi.create({ ...form, status: 'new' });
      toast(`${form.companyName.trim()} submitted for review.`);
      navigate('/app/sales/prospects');
    } catch (err) {
      setError((err as { message?: string }).message ?? 'The account couldn\'t be submitted. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout navItems={salesNavItems}>
      <div className="page">
        <div className="page-header">
          <Button variant="ghost" size="sm" type="button" onClick={() => navigate('/app/sales')}>← Dashboard</Button>
          <h1>Submit account</h1>
          <p className="text-muted">Tell us about a company you&apos;d like to bring on. Our team reviews every submission before onboarding.</p>
        </div>

        <form className="submit-form" onSubmit={handleSubmit}>
          {error && <div className="form-error" role="alert">{error}</div>}

          <Input
            label="Company name"
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            required
          />
          <Input
            label="Contact name"
            value={form.contactName}
            onChange={(e) => setForm({ ...form, contactName: e.target.value })}
          />
          <Input
            label="Contact email"
            type="email"
            value={form.contactEmail}
            onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
          />
          <Input
            label="Contact phone"
            type="tel"
            inputMode="tel"
            value={form.contactPhone}
            onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
          />
          <div className="input-group">
            <label htmlFor="sales-submit-notes" className="input-label">Notes</label>
            <textarea
              id="sales-submit-notes"
              className="input-field"
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Expected volume, routes, special requirements…"
            />
          </div>

          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={() => navigate('/app/sales')}>Cancel</Button>
            <Button type="submit" loading={loading}>Submit account</Button>
          </div>
        </form>

        <style>{`
          .submit-form {
            max-width: 600px;
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-xl);
          }
          .form-error {
            background: #FDE8E8;
            color: var(--color-urgent);
            padding: var(--space-sm) var(--space-md);
            border-radius: var(--radius-sm);
            margin-bottom: var(--space-md);
          }
          .form-actions {
            display: flex;
            gap: var(--space-md);
            margin-top: var(--space-lg);
            flex-wrap: wrap;
          }
          .submit-form textarea.input-field { resize: vertical; font: inherit; }
          @media (max-width: 640px) { .submit-form { padding: var(--space-lg) var(--space-md); } }
        `}</style>
      </div>
    </Layout>
  );
}
